import React, { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Button, Alert, Tab, Stack } from '@mui/material'
import PageTabs from '../components/PageTabs'
import { Plus } from 'lucide-react'
import {
  scheduleAPI,
  repositoriesAPI,
  backupAPI,
  scriptsAPI,
  backupPlansAPI,
} from '../services/api'
import { toast } from 'react-hot-toast'
import { useAnalytics } from '../hooks/useAnalytics'
import { useAuth } from '../hooks/useAuth'
import { usePermissions } from '../hooks/usePermissions'
import { translateBackendKey } from '../utils/translateBackendKey'
import ScheduledChecksSection, {
  ScheduledChecksSectionRef,
} from '../components/ScheduledChecksSection'
import ScheduledRestoreChecksSection, {
  ScheduledRestoreChecksSectionRef,
} from '../components/ScheduledRestoreChecksSection'
import ScheduleWizard, { ScheduleData } from '../components/ScheduleWizard'
import DeleteScheduleDialog from '../components/DeleteScheduleDialog'
import UpcomingJobsTable from '../components/UpcomingJobsTable'
import BackupHistorySection from '../components/BackupHistorySection'
import RunningBackupsSection from '../components/RunningBackupsSection'
import ScheduledJobsTable from '../components/ScheduledJobsTable'
import ActiveBackupPlanRunCard from '../components/ActiveBackupPlanRunCard'
import LogViewerDialog from '../components/LogViewerDialog'
import ScheduleByPlanTab from '../components/ScheduleByPlanTab'
import { type BackupPlanRunLogJob } from '../components/BackupPlanRunsPanel'
import { type BackupJob, type BackupPlan, type BackupPlanRun, Repository } from '../types'
import { useTrackedJobOutcomes } from '../hooks/useTrackedJobOutcomes'
import { getJobDurationSeconds } from '../utils/analyticsProperties'
import { buildScheduleDeepLink, parseScheduleDeepLink } from '../utils/scheduleDeepLink'
import { useLockBreakPermissions } from '../hooks/useLockBreakPermissions'

interface ScheduledJob {
  id: number
  name: string
  cron_expression: string
  timezone?: string | null
  repository: string | null // Legacy single-repo
  repository_id: number | null // Single-repo by ID
  repository_ids: number[] | null // Multi-repo
  enabled: boolean
  last_run: string | null
  next_run: string | null
  created_at: string
  updated_at: string | null
  description: string | null
  archive_name_template: string | null
  run_repository_scripts: boolean // Whether to run per-repository scripts
  pre_backup_script_id: number | null // Schedule-level pre-backup script
  post_backup_script_id: number | null // Schedule-level post-backup script
  run_prune_after: boolean
  run_compact_after: boolean
  prune_keep_hourly: number
  prune_keep_daily: number
  prune_keep_weekly: number
  prune_keep_monthly: number
  prune_keep_quarterly: number
  prune_keep_yearly: number
  prune_keep_within?: string | null
  last_prune: string | null
  last_compact: string | null
}

const SCHEDULE_TAB_PATHS = [
  '/schedule/by-plan',
  '/schedule/checks',
  '/schedule/restore-checks',
  '/schedule/backups',
]
const TAB_BY_PLAN = 0
const TAB_REPO_CHECKS = 1
const TAB_RESTORE_CHECKS = 2
const TAB_BACKUP_JOBS = 3

const Schedule: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const { track, EventCategory, EventAction } = useAnalytics()
  const { hasGlobalPermission } = useAuth()
  const { canDo } = usePermissions()
  const canManageRepositoriesGlobally = hasGlobalPermission('repositories.manage_all')

  const getCurrentTab = React.useCallback(() => {
    if (location.pathname === '/schedule/by-plan') return TAB_BY_PLAN
    if (location.pathname === '/schedule/backups') return TAB_BACKUP_JOBS
    if (location.pathname === '/schedule/checks') return TAB_REPO_CHECKS
    if (location.pathname === '/schedule/restore-checks') return TAB_RESTORE_CHECKS
    return TAB_BY_PLAN
  }, [location.pathname])

  const [currentTab, setCurrentTab] = useState(getCurrentTab())
  const [deleteConfirmJob, setDeleteConfirmJob] = useState<ScheduledJob | null>(null)
  const scheduledChecksSectionRef = useRef<ScheduledChecksSectionRef>(null)
  const scheduledRestoreChecksSectionRef = useRef<ScheduledRestoreChecksSectionRef>(null)

  // Wizard state
  const [showScheduleWizard, setShowScheduleWizard] = useState(false)
  const [wizardMode, setWizardMode] = useState<'create' | 'edit'>('create')
  const [editingJobForWizard, setEditingJobForWizard] = useState<ScheduledJob | undefined>()

  // Backup History filters - load from localStorage
  const [filterSchedule, setFilterSchedule] = useState<number | 'all'>(() => {
    const saved = localStorage.getItem('scheduleBackupHistoryFilterSchedule')
    return saved ? (saved === 'all' ? 'all' : parseInt(saved)) : 'all'
  })
  const [filterRepository, setFilterRepository] = useState<string | 'all'>(() => {
    return localStorage.getItem('scheduleBackupHistoryFilterRepository') || 'all'
  })
  const [filterStatus, setFilterStatus] = useState<string | 'all'>(() => {
    return localStorage.getItem('scheduleBackupHistoryFilterStatus') || 'all'
  })
  const [filterPlan, setFilterPlan] = useState<number | 'all'>(() => {
    const saved = localStorage.getItem('scheduleBackupHistoryFilterPlan')
    return saved ? (saved === 'all' ? 'all' : parseInt(saved)) : 'all'
  })

  useEffect(() => {
    if (location.pathname === '/schedule') {
      navigate('/schedule/by-plan', { replace: true })
    } else if (!SCHEDULE_TAB_PATHS.includes(location.pathname)) {
      navigate('/schedule/by-plan', { replace: true })
    } else {
      setCurrentTab(getCurrentTab())
    }
  }, [getCurrentTab, location.pathname, navigate])

  // Consume deep-link hashes (#repo-checks/<id> or #restore-checks/<id>) once
  // the corresponding section is mounted. Clears the hash afterwards so a
  // refresh doesn't replay the dialog open.
  useEffect(() => {
    const link = parseScheduleDeepLink(location.hash)
    if (!link) return
    const onCheckTab = link.target === 'repo-checks' && location.pathname === '/schedule/checks'
    const onRestoreTab =
      link.target === 'restore-checks' && location.pathname === '/schedule/restore-checks'
    if (!onCheckTab && !onRestoreTab) return
    const timer = setTimeout(() => {
      if (onCheckTab) {
        scheduledChecksSectionRef.current?.openEditForRepo(link.repositoryId)
      } else if (onRestoreTab) {
        scheduledRestoreChecksSectionRef.current?.openEditForRepo(link.repositoryId)
      }
      navigate(location.pathname, { replace: true })
    }, 50)
    return () => clearTimeout(timer)
  }, [location.hash, location.pathname, navigate])

  // Save filter state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('scheduleBackupHistoryFilterSchedule', String(filterSchedule))
  }, [filterSchedule])

  useEffect(() => {
    localStorage.setItem('scheduleBackupHistoryFilterRepository', filterRepository)
  }, [filterRepository])

  useEffect(() => {
    localStorage.setItem('scheduleBackupHistoryFilterStatus', filterStatus)
  }, [filterStatus])

  useEffect(() => {
    localStorage.setItem('scheduleBackupHistoryFilterPlan', String(filterPlan))
  }, [filterPlan])

  // Get scheduled jobs
  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['scheduled-jobs'],
    queryFn: scheduleAPI.getScheduledJobs,
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  // Get repositories
  const { data: repositoriesData, isLoading: loadingRepositories } = useQuery({
    queryKey: ['repositories'],
    queryFn: repositoriesAPI.getRepositories,
  })

  const repositories = React.useMemo<Repository[]>(
    () => repositoriesData?.data?.repositories ?? [],
    [repositoriesData?.data?.repositories]
  )
  const manageableRepositories = repositories.filter((repo: Repository) =>
    canDo(repo.id, 'maintenance')
  )
  const { canBreakLock: canBreakLockForBackupJob, lockBreakingEnabled } = useLockBreakPermissions({
    repositories,
  })

  // Backup job history — fetch all jobs (not just scheduled) so orphaned/manual
  // history remains visible after a legacy schedule is deleted.
  const { data: backupJobsData, isLoading: loadingBackupJobs } = useQuery({
    queryKey: ['backup-jobs-all'],
    queryFn: backupAPI.getAllJobs,
    refetchInterval: 3000,
  })

  // Get scripts library
  const { data: scriptsData } = useQuery({
    queryKey: ['scripts'],
    queryFn: () => scriptsAPI.list(),
  })

  // Get upcoming jobs
  const { data: upcomingData } = useQuery({
    queryKey: ['upcoming-jobs'],
    queryFn: () => scheduleAPI.getUpcomingJobs(24),
    refetchInterval: 60000, // Refresh every minute
  })

  // Get backup plans (for displaying active plan runs)
  const { data: backupPlansData } = useQuery({
    queryKey: ['backup-plans'],
    queryFn: () => backupPlansAPI.list(),
  })
  const { data: backupPlanRunsData } = useQuery({
    queryKey: ['backup-plan-runs'],
    queryFn: () => backupPlansAPI.listRuns(),
    refetchInterval: 2000,
  })
  const backupPlans = React.useMemo<BackupPlan[]>(
    () => backupPlansData?.data?.backup_plans ?? [],
    [backupPlansData?.data?.backup_plans]
  )
  const backupPlanRuns = React.useMemo<BackupPlanRun[]>(
    () => backupPlanRunsData?.data?.runs ?? [],
    [backupPlanRunsData?.data?.runs]
  )
  const activePlanRunsWithPlans = React.useMemo(() => {
    return backupPlanRuns
      .filter((run) => run.status === 'pending' || run.status === 'running')
      .map((run) => {
        const plan = backupPlans.find((p) => p.id === run.backup_plan_id)
        return plan ? { run, plan } : null
      })
      .filter((pair): pair is { run: BackupPlanRun; plan: BackupPlan } => pair !== null)
  }, [backupPlanRuns, backupPlans])

  const [logJob, setLogJob] = useState<BackupPlanRunLogJob | null>(null)
  const [cancellingPlanRunId, setCancellingPlanRunId] = useState<number | null>(null)
  const cancelPlanRunMutation = useMutation({
    mutationFn: (runId: number) => backupPlansAPI.cancelRun(runId),
    onMutate: (runId) => setCancellingPlanRunId(runId),
    onSettled: () => setCancellingPlanRunId(null),
    onSuccess: () => {
      toast.success(t('backupPlans.toasts.cancelled'))
      queryClient.invalidateQueries({ queryKey: ['backup-plan-runs'] })
      queryClient.invalidateQueries({ queryKey: ['backup-plans'] })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('backupPlans.toasts.cancelFailed')
      )
    },
  })

  // Create job mutation
  const createJobMutation = useMutation({
    mutationFn: scheduleAPI.createScheduledJob,
    onSuccess: () => {
      toast.success(t('schedule.toasts.jobCreated'))
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['upcoming-jobs'] })
      track(EventCategory.BACKUP, EventAction.CREATE, { entity: 'schedule' })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('schedule.toasts.jobCreateFailed')
      )
    },
  })

  // Update job mutation
  const updateJobMutation = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      scheduleAPI.updateScheduledJob(id, data),
    onSuccess: () => {
      toast.success(t('schedule.toasts.jobUpdated'))
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['upcoming-jobs'] })
      track(EventCategory.BACKUP, EventAction.EDIT, { entity: 'schedule' })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('schedule.toasts.jobUpdateFailed')
      )
    },
  })

  // Delete job mutation
  const deleteJobMutation = useMutation({
    mutationFn: scheduleAPI.deleteScheduledJob,
    onSuccess: () => {
      toast.success(t('schedule.toasts.jobDeleted'))
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['upcoming-jobs'] })
      setDeleteConfirmJob(null)
      track(EventCategory.BACKUP, EventAction.DELETE, { entity: 'schedule' })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('schedule.toasts.jobDeleteFailed')
      )
    },
  })

  // Toggle job mutation
  const toggleJobMutation = useMutation({
    mutationFn: (job: ScheduledJob) => scheduleAPI.toggleScheduledJob(job.id),
    onSuccess: (_response, job) => {
      toast.success(t('schedule.toasts.jobStatusUpdated'))
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['upcoming-jobs'] })
      track(EventCategory.BACKUP, EventAction.EDIT, {
        entity: 'schedule',
        operation: 'toggle',
        enabled: !job.enabled,
      })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('schedule.toasts.jobToggleFailed')
      )
    },
  })

  // Run job now mutation
  const runJobNowMutation = useMutation({
    mutationFn: (job: ScheduledJob) => scheduleAPI.runScheduledJobNow(job.id),
    onSuccess: (_response, job) => {
      toast.success(t('schedule.toasts.jobStarted'))
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['backup-status'] })
      queryClient.invalidateQueries({ queryKey: ['backup-jobs-all'] })
      track(EventCategory.BACKUP, EventAction.START, {
        entity: 'schedule',
        trigger: 'manual',
        repository_count: job.repository_ids?.length || (job.repository_id ? 1 : 0),
      })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('schedule.toasts.jobRunFailed')
      )
    },
  })

  // Duplicate job mutation
  const duplicateJobMutation = useMutation({
    mutationFn: (job: ScheduledJob) => scheduleAPI.duplicateScheduledJob(job.id),
    onSuccess: (_response, job) => {
      toast.success(t('schedule.toasts.jobDuplicated'))
      queryClient.invalidateQueries({ queryKey: ['scheduled-jobs'] })
      queryClient.invalidateQueries({ queryKey: ['upcoming-jobs'] })
      track(EventCategory.BACKUP, EventAction.CREATE, {
        entity: 'schedule',
        source: 'duplicate',
        repository_count: job.repository_ids?.length || (job.repository_id ? 1 : 0),
      })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('schedule.toasts.jobDuplicateFailed')
      )
    },
  })

  // Cancel backup job mutation
  const cancelBackupMutation = useMutation({
    mutationFn: (jobId: string) => backupAPI.cancelJob(jobId),
    onSuccess: () => {
      toast.success(t('schedule.toasts.backupCancelled'))
      queryClient.invalidateQueries({ queryKey: ['backup-jobs-all'] })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('schedule.toasts.backupCancelFailed')
      )
    },
  })

  const handleDeleteJob = () => {
    if (deleteConfirmJob) {
      deleteJobMutation.mutate(deleteConfirmJob.id)
    }
  }

  const handleToggleJob = (job: ScheduledJob) => {
    toggleJobMutation.mutate(job)
  }

  const handleRunJobNow = (job: ScheduledJob) => {
    if (window.confirm(t('schedule.confirmRunNow', { name: job.name }))) {
      runJobNowMutation.mutate(job)
    }
  }

  const handleDuplicateJob = (job: ScheduledJob) => {
    duplicateJobMutation.mutate(job)
  }

  useTrackedJobOutcomes<BackupJob>({
    jobs: backupJobsData?.data?.jobs?.filter((job: BackupJob) => job.scheduled_job_id),
    onTerminal: (job) => {
      const scheduledJob = jobsData?.data?.jobs?.find(
        (candidate: ScheduledJob) => candidate.id === job.scheduled_job_id
      )
      const action =
        job.status === 'completed' || job.status === 'completed_with_warnings'
          ? EventAction.COMPLETE
          : EventAction.FAIL

      track(EventCategory.BACKUP, action, {
        entity: 'schedule',
        scheduled_job_id: job.scheduled_job_id,
        schedule_name: scheduledJob?.name ?? null,
        repository_count:
          scheduledJob?.repository_ids?.length || (scheduledJob?.repository_id ? 1 : 0),
        status: job.status,
        job_id: job.id,
        trigger: 'scheduled',
        maintenance_status: job.maintenance_status ?? null,
        duration_seconds: getJobDurationSeconds(job.started_at, job.completed_at),
        error_present: !!job.error_message,
        warning_count: job.status === 'completed_with_warnings' ? 1 : 0,
      })
    },
  })

  // Wizard handlers
  const openCreateWizard = () => {
    setWizardMode('create')
    setEditingJobForWizard(undefined)
    setShowScheduleWizard(true)
  }

  const openEditWizard = (job: ScheduledJob) => {
    setWizardMode('edit')
    setEditingJobForWizard(job)
    setShowScheduleWizard(true)
  }

  const handleWizardSubmit = (data: ScheduleData) => {
    if (wizardMode === 'create') {
      createJobMutation.mutate(data)
    } else if (wizardMode === 'edit' && editingJobForWizard) {
      updateJobMutation.mutate({
        id: editingJobForWizard.id,
        data,
      })
    }
    setShowScheduleWizard(false)
  }

  const getRepositoryName = (path: string) => {
    const repo = repositories?.find((r: Repository) => r.path === path)
    return repo?.name || path
  }

  const canManageJob = React.useCallback(
    (job: ScheduledJob) => {
      const targetRepoIds = job.repository_ids?.length
        ? job.repository_ids
        : job.repository_id
          ? [job.repository_id]
          : repositories
              .filter((repo: Repository) => repo.path === job.repository)
              .map((repo: Repository) => repo.id)
      if (targetRepoIds.length === 0) return canManageRepositoriesGlobally
      return targetRepoIds.every((repoId: number) => canDo(repoId, 'maintenance'))
    },
    [canDo, canManageRepositoriesGlobally, repositories]
  )

  const canCreateSchedule = canManageRepositoriesGlobally || manageableRepositories.length > 0

  const jobs = jobsData?.data?.jobs || []
  const allBackupJobs = backupJobsData?.data?.jobs || []
  const runningBackupJobs = allBackupJobs.filter(
    (job: BackupJob) =>
      job.status === 'running' ||
      (job.maintenance_status && job.maintenance_status.includes('running'))
  )
  const upcomingJobs = upcomingData?.data?.upcoming_jobs || []

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 600,
            }}
          >
            {t('schedule.title')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('schedule.subtitle')}
          </Typography>
        </Box>

        {/* Action Button — hidden for viewers */}
        {canCreateSchedule && currentTab === TAB_BY_PLAN && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              variant="outlined"
              startIcon={<Plus size={18} />}
              onClick={openCreateWizard}
              disabled={!canCreateSchedule}
              fullWidth
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {t('schedule.createLegacySchedule', {
                defaultValue: 'Create backup automation',
              })}
            </Button>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => navigate('/backup-plans')}
              disabled={!canCreateSchedule}
              fullWidth
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {t('schedule.createBackupPlan')}
            </Button>
          </Stack>
        )}
        {canCreateSchedule && currentTab === TAB_REPO_CHECKS && (
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => scheduledChecksSectionRef.current?.openAddDialog()}
            disabled={!canCreateSchedule}
            fullWidth
            sx={{
              width: { xs: '100%', sm: 'auto' },
              alignSelf: { xs: 'stretch', sm: 'auto' },
            }}
          >
            {t('schedule.addCheck')}
          </Button>
        )}
        {canCreateSchedule && currentTab === TAB_RESTORE_CHECKS && (
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => scheduledRestoreChecksSectionRef.current?.openAddDialog()}
            disabled={!canCreateSchedule}
            fullWidth
            sx={{
              width: { xs: '100%', sm: 'auto' },
              alignSelf: { xs: 'stretch', sm: 'auto' },
            }}
          >
            {t('schedule.addRestoreCheck')}
          </Button>
        )}
      </Box>

      <PageTabs
        value={currentTab}
        onChange={(_, newValue: number) => {
          setCurrentTab(newValue)
          navigate(SCHEDULE_TAB_PATHS[newValue] || SCHEDULE_TAB_PATHS[0])
        }}
        scrollable
      >
        <Tab label={t('schedule.tabs.byPlan', { defaultValue: 'By Plan' })} />
        <Tab label={t('schedule.tabs.repositoryChecks')} />
        <Tab label={t('schedule.tabs.restoreChecks')} />
        <Tab label={t('schedule.tabs.backupJobs')} />
      </PageTabs>

      {!loadingRepositories && (!repositories || repositories.length === 0) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('schedule.noRepositories')}
        </Alert>
      )}

      {currentTab === TAB_BY_PLAN && (
        <ScheduleByPlanTab
          plans={backupPlans}
          repositories={repositories}
          isLoading={loadingRepositories}
          onEditPlan={(planId) => navigate(`/backup-plans#backup-plan-${planId}`)}
          onEditRepoCheck={(repoId) => {
            navigate(`/schedule/checks${buildScheduleDeepLink('repo-checks', repoId)}`)
          }}
          onEditRepoRestore={(repoId) => {
            navigate(`/schedule/restore-checks${buildScheduleDeepLink('restore-checks', repoId)}`)
          }}
          canManageRepo={(repoId) => canDo(repoId, 'maintenance')}
          canManagePlan={() => canManageRepositoriesGlobally}
          legacySection={
            (isLoading || jobs.length > 0) && (
              <ScheduledJobsTable
                jobs={jobs}
                repositories={repositories}
                isLoading={isLoading}
                title={t('schedule.legacyBackupSchedulesTitle')}
                description={t('schedule.legacyBackupSchedulesDescription')}
                canManageJob={canManageJob}
                onEdit={openEditWizard}
                onDelete={setDeleteConfirmJob}
                onDuplicate={handleDuplicateJob}
                onRunNow={handleRunJobNow}
                onToggle={handleToggleJob}
                isRunNowPending={runJobNowMutation.isPending}
                isDuplicatePending={duplicateJobMutation.isPending}
              />
            )
          }
        />
      )}

      {currentTab === TAB_BACKUP_JOBS && (
        <Box>
          {activePlanRunsWithPlans.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack spacing={2}>
                {activePlanRunsWithPlans.map(({ run, plan }) => (
                  <ActiveBackupPlanRunCard
                    key={run.id}
                    run={run}
                    plan={plan}
                    cancelling={cancellingPlanRunId === run.id}
                    onCancel={(runId) => cancelPlanRunMutation.mutate(runId)}
                    onViewLogs={(job) => setLogJob(job)}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <RunningBackupsSection
            runningBackupJobs={runningBackupJobs}
            onCancelBackup={(jobId) => cancelBackupMutation.mutate(String(jobId))}
            isCancelling={cancelBackupMutation.isPending}
          />

          <UpcomingJobsTable
            upcomingJobs={upcomingJobs}
            repositories={repositories}
            isLoading={isLoading}
            getRepositoryName={getRepositoryName}
            onPlanClick={(planId) => navigate(`/backup-plans#backup-plan-${planId}`)}
          />

          <BackupHistorySection
            backupJobs={allBackupJobs}
            scheduledJobs={jobs}
            backupPlans={backupPlans}
            repositories={repositories}
            isLoading={loadingBackupJobs}
            canBreakLocks={canBreakLockForBackupJob}
            lockBreakingEnabled={lockBreakingEnabled}
            canDeleteJobs={canManageRepositoriesGlobally}
            filterSchedule={filterSchedule}
            filterRepository={filterRepository}
            filterStatus={filterStatus}
            filterPlan={filterPlan}
            onFilterScheduleChange={setFilterSchedule}
            onFilterRepositoryChange={setFilterRepository}
            onFilterStatusChange={setFilterStatus}
            onFilterPlanChange={setFilterPlan}
          />
        </Box>
      )}

      {currentTab === TAB_REPO_CHECKS && (
        <Box>
          <ScheduledChecksSection ref={scheduledChecksSectionRef} />
        </Box>
      )}

      {currentTab === TAB_RESTORE_CHECKS && (
        <Box>
          <ScheduledRestoreChecksSection ref={scheduledRestoreChecksSectionRef} />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteScheduleDialog
        open={!!deleteConfirmJob}
        job={deleteConfirmJob}
        onClose={() => setDeleteConfirmJob(null)}
        onConfirm={handleDeleteJob}
        isDeleting={deleteJobMutation.isPending}
      />

      {/* Schedule Wizard */}
      <ScheduleWizard
        open={showScheduleWizard}
        onClose={() => setShowScheduleWizard(false)}
        mode={wizardMode}
        scheduledJob={editingJobForWizard}
        repositories={manageableRepositories || []}
        scripts={scriptsData?.data || []}
        onSubmit={handleWizardSubmit}
      />

      <LogViewerDialog job={logJob} open={Boolean(logJob)} onClose={() => setLogJob(null)} />
    </Box>
  )
}

export default Schedule
